
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { submitFeedback } from '../services/feedbackService';
import { MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const FeedbackForm = () => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'suggestion' | 'bug' | 'question' | 'other'>('suggestion');
  const [anonymousName, setAnonymousName] = useState('');
  const [anonymousEmail, setAnonymousEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) {
      toast({
        title: t('feedback.missingInfo'),
        description: t('feedback.titleDescRequired'),
        variant: "destructive",
      });
      return;
    }

    // For anonymous users, require name and email
    if (!user && (!anonymousName.trim() || !anonymousEmail.trim())) {
      toast({
        title: t('feedback.missingInfo'),
        description: t('feedback.nameEmailRequired'),
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await submitFeedback({ 
        title, 
        description, 
        type,
        anonymous_name: !user ? anonymousName : undefined,
        anonymous_email: !user ? anonymousEmail : undefined
      });
      
      // Clear the form
      setTitle('');
      setDescription('');
      setType('suggestion');
      setAnonymousName('');
      setAnonymousEmail('');
      
      toast({
        title: t('feedback.thankYou'),
        description: t('feedback.thankYouDesc'),
      });
    } catch (error: any) {
      toast({
        title: t('feedback.submitError'),
        description: error.message || "Something went wrong. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="feedback-type">{t('feedback.type')}</Label>
        <Select value={type} onValueChange={(value: 'suggestion' | 'bug' | 'question' | 'other') => setType(value)}>
          <SelectTrigger id="feedback-type" className="w-full">
            <SelectValue placeholder={t('feedback.selectType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="suggestion">{t('feedback.types.suggestion')}</SelectItem>
            <SelectItem value="bug">{t('feedback.types.bug')}</SelectItem>
            <SelectItem value="question">{t('feedback.types.question')}</SelectItem>
            <SelectItem value="other">{t('feedback.types.other')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!user && (
        <>
          <div className="space-y-2">
            <Label htmlFor="anonymous-name">{t('feedback.yourName')}</Label>
            <Input
              id="anonymous-name"
              placeholder={t('feedback.namePlaceholder')}
              value={anonymousName}
              onChange={(e) => setAnonymousName(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="anonymous-email">{t('feedback.yourEmail')}</Label>
            <Input
              id="anonymous-email"
              type="email"
              placeholder={t('feedback.emailPlaceholder')}
              value={anonymousEmail}
              onChange={(e) => setAnonymousEmail(e.target.value)}
              required
            />
          </div>
        </>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="feedback-title">{t('feedback.title')}</Label>
        <Input
          id="feedback-title"
          placeholder={t('feedback.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="feedback-description">{t('feedback.description')}</Label>
        <Textarea
          id="feedback-description"
          placeholder={t('feedback.descriptionPlaceholder')}
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-pet-purple hover:bg-pet-purple/90"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          t('feedback.submitting')
        ) : (
          <>
            <MessageSquare className="mr-2 h-4 w-4" />
            {t('feedback.submit')}
          </>
        )}
      </Button>
    </form>
  );
};

export default FeedbackForm;
